'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2, Calendar, MapPin, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { eventsApi } from '@/lib/api';
import Link from 'next/link';

export function DashboardSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.querySelector('input')?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (query.trim().length < 2) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const res = await eventsApi.search(query);
        const data = res.data?.data ?? [];
        setResults(Array.isArray(data) ? data : []);
        setIsOpen(true);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div ref={searchRef} className="relative w-full max-w-md mx-6">
      <div className="relative group">
        <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-nature-muted group-focus-within:text-primary transition-colors" />
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim().length >= 2 && setIsOpen(true)}
          placeholder="Search events, users, or settings..."
          className="w-full h-10 pl-10 pr-12 bg-transparent border border-nature-border/80 rounded-full text-sm placeholder:text-nature-muted focus:bg-bg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all duration-200"
        />
        <div className="absolute inset-y-0 right-3.5 flex items-center">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (results.length > 0 || query.trim().length >= 2) && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            className="absolute top-full left-0 right-0 mt-2 z-50 overflow-hidden rounded-2xl border border-nature-border bg-bg shadow-xl"
          >
            <div className="max-h-[min(400px,70vh)] overflow-y-auto p-2">
              {results.length > 0 ? (
                <>
                  <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-nature-muted">
                    Events Found
                  </div>
                  <div className="space-y-1">
                    {results.slice(0, 5).map((event: any) => (
                      <button
                        key={event.id}
                        onClick={() => {
                          router.push(`/dashboard/events/${event.id}/overview`);
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center gap-3 p-2.5 rounded-xl transition-all hover:bg-nature-bg text-left group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-nature-soft flex items-center justify-center shrink-0">
                          <Calendar className="w-5 h-5 text-nature-text group-hover:text-primary transition-colors" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-nature-text truncate">{event.title}</h4>
                          <div className="flex items-center gap-2 mt-0.5 text-xs text-nature-muted">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location || 'Online'}
                            </span>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-nature-muted opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" />
                      </button>
                    ))}
                  </div>
                </>
              ) : !loading && (
                <div className="p-8 text-center">
                  <p className="text-sm text-nature-muted">No results found for "{query}"</p>
                </div>
              )}
            </div>
            
            <div className="p-3 border-t border-nature-border bg-nature-bg/30 text-center">
              <Link 
                href={`/search?q=${encodeURIComponent(query)}`}
                className="text-xs font-semibold text-primary hover:underline transition-all"
                onClick={() => setIsOpen(false)}
              >
                View all search results
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
